import { Anchor, Box, Group, Stack, Table, Text } from '@mantine/core';
import { IconArrowRight } from '@tabler/icons-react';
import { Fragment, useState, type ReactNode } from 'react';
import type { DiffGroup, DiffRow } from '../../domain/entityDiff.js';
import { useIsMobile } from '../../hooks/useIsMobile.js';
import { ValueChips } from './ValueChips.js';
import { ValueText } from './ValueText.js';

/**
 * A diff as a list of fields, never a document.
 *
 * `headings` is Field, then the before side, then the after side. Before is always what the
 * environment holds now and after is what a push writes; a changed row tints before in drift and
 * after in sync, with an arrow between. The arrow is the direction the data moves:
 * `flow="state"` (a push record, `Before` → `After`) puts before on the left, as the
 * environment's value before and after the write; `flow="source"` (the drawer, `Master` →
 * `<env> now`) puts after on the left, as the value that travels from master into the
 * environment. `afterOnly` drops the before column: a create, or an operation stored without its
 * before values. Below `sm` each row stacks: the label, then the two sides in the same order.
 *
 * A list that changes splits the same way: the items only the environment has (removed) under
 * before, the items only the after side has (added) under after, and the items both share folded
 * into an "N unchanged" toggle — a one-item change never drowns in forty identical chips.
 *
 * `rowAction` puts one control on a field's first row — the drawer's per-field **Adopt** — in a
 * trailing column above `sm` and beside the label below it. A field it returns nothing for gets
 * an empty cell, so the columns still line up.
 */
export type DiffHeadings = [field: ReactNode, before: ReactNode, after: ReactNode];

type Props = {
    groups: DiffGroup[];
    headings: DiffHeadings;
    afterOnly?: boolean;
    flow?: 'state' | 'source';
    rowAction?: (group: DiffGroup) => ReactNode;
};

/** The two sides in reading order, left (or top) first. */
const ordered = <T,>(flow: Props['flow'], before: T, after: T): [T, T] =>
    flow === 'source' ? [after, before] : [before, after];

/** The row's field cell: the group's label on its first row, then the leaf's path after ` › `. */
const FieldCell = ({ group, row, first }: { group: DiffGroup; row: DiffRow; first: boolean }) => (
    <FieldLabel group={group} rest={row.path.slice(1)} first={first} />
);

/** The trailing action cell, on a field's first row only. */
const ActionCell = ({
    group,
    first,
    rowAction,
}: {
    group: DiffGroup;
    first: boolean;
    rowAction: Props['rowAction'];
}) =>
    rowAction ? (
        <Table.Td px={0} style={{ verticalAlign: 'top', textAlign: 'right' }}>
            {first && rowAction(group)}
        </Table.Td>
    ) : null;

const FieldLabel = ({ group, rest, first }: { group: DiffGroup; rest: string[]; first: boolean }) => (
    <Text fz="xs" style={{ wordBreak: 'break-word' }}>
        {first && <span style={{ fontWeight: 500 }}>{group.label}</span>}
        {rest.length > 0 && (
            <Text span fz="xs" c="dimmed">
                {first ? ' › ' : '› '}
                {rest.map((key, i) => (
                    <Fragment key={i}>
                        {i > 0 && ' › '}
                        <span className="rgs-numeric">{key}</span>
                    </Fragment>
                ))}
            </Text>
        )}
    </Text>
);

/** A top-level field's kind applies to its own value only; a nested leaf reads as plain text. */
const kindOf = (group: DiffGroup, row: DiffRow) => (row.path.length === 1 ? group.kind : undefined);

/** A row that changes carries the tints and the arrow; an unchanged one (All fields) stays plain. */
const changes = (row: DiffRow) => row.kind === 'scalar' && row.change !== 'unchanged';

type Chips = Extract<DiffRow, { kind: 'chips' }>;
const chipsChange = (row: Chips) => row.items.some((item) => item.side !== 'both');

/** One side of a changed list: its own items, or a dimmed line saying there are none. */
const ChipSide = ({ items, none }: { items: Chips['items']; none: string }) =>
    items.length > 0 ? (
        <ValueChips items={items} />
    ) : (
        <Text fz="xs" c="dimmed">
            {none}
        </Text>
    );

/** The items both sides share, folded; open state is the row's own, so it survives a re-render. */
const UnchangedChips = ({ items }: { items: Chips['items'] }) => {
    const [open, setOpen] = useState(false);
    if (items.length === 0) return null;
    return (
        <Stack gap={4}>
            <Anchor
                component="button"
                type="button"
                fz="xs"
                c="dimmed"
                w="fit-content"
                aria-expanded={open}
                onClick={() => setOpen((v) => !v)}
            >
                {open ? 'Hide unchanged' : `${items.length} unchanged`}
            </Anchor>
            {open && <ValueChips items={items} />}
        </Stack>
    );
};

const Arrow = () => <IconArrowRight size={12} stroke={1.8} color="var(--mantine-color-dimmed)" aria-hidden />;

/**
 * One row's before/after content, built once so `DiffTable` and `DiffStack` only arrange it —
 * they used to each build the scalar pair, the chip pair and the `side === 'both'` filter over
 * again. A changed list gets its own `unchanged` node (the fold-away chips); a list with no
 * differing items collapses to a single `value` spanning both sides, same as `afterOnly`.
 */
type RowSides =
    | { kind: 'value'; before: ReactNode; after: ReactNode; changed: boolean }
    | { kind: 'chipsChanged'; before: ReactNode; after: ReactNode; unchanged: ReactNode }
    | { kind: 'chipsSame'; value: ReactNode };

const rowSides = (group: DiffGroup, row: DiffRow, afterOnly: boolean | undefined): RowSides => {
    if (row.kind === 'chips') {
        if (!afterOnly && chipsChange(row)) {
            return {
                kind: 'chipsChanged',
                before: <ChipSide items={row.items.filter((i) => i.side === 'removed')} none="Nothing removed" />,
                after: <ChipSide items={row.items.filter((i) => i.side === 'added')} none="Nothing added" />,
                unchanged: <UnchangedChips items={row.items.filter((i) => i.side === 'both')} />,
            };
        }
        return { kind: 'chipsSame', value: <ValueChips items={row.items} /> };
    }
    return {
        kind: 'value',
        before: (
            <ValueText value={row.before} kind={kindOf(group, row)} dimmed tone={changes(row) ? 'before' : undefined} />
        ),
        after: (
            <ValueText
                value={row.after}
                kind={kindOf(group, row)}
                tone={!afterOnly && changes(row) ? 'after' : undefined}
            />
        ),
        changed: changes(row),
    };
};

const flatten = (groups: DiffGroup[]) =>
    groups.flatMap((group) => group.rows.map((row, i) => ({ group, row, first: i === 0, key: `${group.field}:${i}` })));

const DiffTable = ({ groups, headings, afterOnly, flow, rowAction }: Props) => {
    const [firstHeading, secondHeading] = ordered(flow, headings[1], headings[2]);
    return (
        <Box>
            <Table verticalSpacing={4} layout="fixed">
                <Table.Thead>
                    <Table.Tr>
                        <Table.Th w={afterOnly ? '40%' : '30%'}>{headings[0]}</Table.Th>
                        {afterOnly ? (
                            <Table.Th>{headings[2]}</Table.Th>
                        ) : (
                            <>
                                <Table.Th>{firstHeading}</Table.Th>
                                <Table.Th w={24} aria-hidden />
                                <Table.Th>{secondHeading}</Table.Th>
                            </>
                        )}
                        {rowAction && <Table.Th w={84} aria-hidden />}
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    {flatten(groups).map(({ group, row, first, key }) => {
                        const sides = rowSides(group, row, afterOnly);
                        if (sides.kind === 'chipsChanged') {
                            const [left, right] = ordered(flow, sides.before, sides.after);
                            return (
                                <Fragment key={key}>
                                    <Table.Tr style={{ borderBottom: 0 }}>
                                        <Table.Td style={{ verticalAlign: 'top' }}>
                                            <FieldCell group={group} row={row} first={first} />
                                        </Table.Td>
                                        <Table.Td style={{ verticalAlign: 'top' }}>{left}</Table.Td>
                                        <Table.Td px={0} style={{ verticalAlign: 'top', textAlign: 'center' }}>
                                            <Arrow />
                                        </Table.Td>
                                        <Table.Td style={{ verticalAlign: 'top' }}>{right}</Table.Td>
                                        <ActionCell group={group} first={first} rowAction={rowAction} />
                                    </Table.Tr>
                                    <Table.Tr>
                                        <Table.Td />
                                        <Table.Td colSpan={rowAction ? 4 : 3} pt={0}>
                                            {sides.unchanged}
                                        </Table.Td>
                                    </Table.Tr>
                                </Fragment>
                            );
                        }
                        if (sides.kind === 'chipsSame') {
                            return (
                                <Table.Tr key={key}>
                                    <Table.Td style={{ verticalAlign: 'top' }}>
                                        <FieldCell group={group} row={row} first={first} />
                                    </Table.Td>
                                    <Table.Td colSpan={afterOnly ? 1 : 3}>{sides.value}</Table.Td>
                                    <ActionCell group={group} first={first} rowAction={rowAction} />
                                </Table.Tr>
                            );
                        }
                        const [left, right] = ordered(flow, sides.before, sides.after);
                        return (
                            <Table.Tr key={key}>
                                <Table.Td style={{ verticalAlign: 'top' }}>
                                    <FieldCell group={group} row={row} first={first} />
                                </Table.Td>
                                {afterOnly ? (
                                    <Table.Td style={{ verticalAlign: 'top' }}>{sides.after}</Table.Td>
                                ) : (
                                    <>
                                        <Table.Td style={{ verticalAlign: 'top' }}>{left}</Table.Td>
                                        <Table.Td px={0} style={{ verticalAlign: 'top', textAlign: 'center' }}>
                                            {sides.changed && <Arrow />}
                                        </Table.Td>
                                        <Table.Td style={{ verticalAlign: 'top' }}>{right}</Table.Td>
                                    </>
                                )}
                                <ActionCell group={group} first={first} rowAction={rowAction} />
                            </Table.Tr>
                        );
                    })}
                </Table.Tbody>
            </Table>
        </Box>
    );
};

const Side = ({ heading, children }: { heading: ReactNode; children: ReactNode }) => (
    <Box style={{ display: 'grid', gridTemplateColumns: 'minmax(56px, auto) 1fr', columnGap: 8 }}>
        <Text fz={10} c="dimmed" tt="uppercase" fw={600} pt={2} style={{ letterSpacing: '0.04em' }}>
            {heading}
        </Text>
        <Box miw={0}>{children}</Box>
    </Box>
);

/** The two sides stacked in reading order, the second led by the arrow when the row changes. */
const StackedSides = ({
    flow,
    headings,
    before,
    after,
    arrow,
}: {
    flow: Props['flow'];
    headings: DiffHeadings;
    before: ReactNode;
    after: ReactNode;
    arrow: boolean;
}) => {
    const [[firstHeading, firstValue], [secondHeading, secondValue]] = ordered(
        flow,
        [headings[1], before] as const,
        [headings[2], after] as const,
    );
    return (
        <>
            <Side heading={firstHeading}>{firstValue}</Side>
            <Side heading={secondHeading}>
                <Group gap={6} wrap="nowrap" align="flex-start">
                    {arrow && <Arrow />}
                    {secondValue}
                </Group>
            </Side>
        </>
    );
};

const DiffStack = ({ groups, headings, afterOnly, flow, rowAction }: Props) => (
    <Stack gap={0}>
        {flatten(groups).map(({ group, row, first, key }) => {
            const sides = rowSides(group, row, afterOnly);
            return (
                <Stack key={key} gap={2} py={6} style={{ borderBottom: '1px solid var(--rgs-line)' }}>
                    <Group gap="xs" justify="space-between" wrap="nowrap" align="flex-start">
                        <FieldCell group={group} row={row} first={first} />
                        {first && rowAction?.(group)}
                    </Group>
                    {sides.kind === 'chipsChanged' ? (
                        <>
                            <StackedSides
                                flow={flow}
                                headings={headings}
                                arrow
                                before={sides.before}
                                after={sides.after}
                            />
                            {sides.unchanged}
                        </>
                    ) : sides.kind === 'chipsSame' ? (
                        sides.value
                    ) : afterOnly ? (
                        <Side heading={headings[2]}>{sides.after}</Side>
                    ) : (
                        <StackedSides
                            flow={flow}
                            headings={headings}
                            arrow={sides.changed}
                            before={sides.before}
                            after={sides.after}
                        />
                    )}
                </Stack>
            );
        })}
    </Stack>
);

export const DiffGroups = (props: Props) => (useIsMobile() ? <DiffStack {...props} /> : <DiffTable {...props} />);
